import { RefreshCw, Upload, Database } from 'lucide-react';
import { memo, useMemo } from 'react';

type LoadingType = 'upload' | 'update' | 'main';

interface LoadingStageProps {
  LoadingType: LoadingType;
}

const LoadingStage = memo(({ LoadingType }: LoadingStageProps) => {
  // Memoize the message to prevent unnecessary recalculations
  const message = useMemo(() => {
    switch (LoadingType) {
      case 'upload':
        return 'جاري الان رفع طلبية الشراء الي السيرفر';
      case 'update':
        return 'جاري تحديث البيانات';
      case 'main':
        return 'جاري تحميل اسماء الموردين واجراء العمليات الاساسية علي الاصناف';
      default:
        return '';
    }
  }, [LoadingType]);

  // Memoize icon selection based on loading type
  const LoadingIcon = useMemo(() => {
    switch (LoadingType) {
      case 'upload':
        return Upload;
      case 'update':
        return RefreshCw;
      case 'main':
      default:
        return Database;
    }
  }, [LoadingType]);

  // Memoize background style classes
  const backgroundClasses = useMemo(
    () =>
      LoadingType === 'main'
        ? 'bg-white/90 backdrop-blur-3xl dark:bg-black/90'
        : 'bg-white/80 backdrop-blur-sm dark:bg-black/80',
    [LoadingType],
  );

  return (
    <div
      className={`fixed inset-0 z-[5000] flex flex-col items-center justify-center ${backgroundClasses} transition-all duration-300`}
      aria-live="polite"
      aria-busy="true"
    >
      <div className="relative flex flex-col items-center">
        {/* Modern pulsing glow effect */}
        <div className="absolute inset-0 -z-10 animate-pulse-slow rounded-full bg-primary/10 blur-xl" />

        {/* Primary loader animation */}
        <div className="relative flex h-24 w-24 items-center justify-center">
          {/* Spinning outer circle */}
          <div className="absolute h-full w-full animate-spin-slow rounded-full border-b-2 border-t-2 border-primary/30" />

          {/* Spinning inner circle - opposite direction */}
          <div className="absolute h-4/5 w-4/5 animate-reverse-spin rounded-full border-r-2 border-primary/40" />

          {/* Center icon with pulse animation */}
          <div className="animate-bounce-subtle">
            <LoadingIcon className="h-8 w-8 text-primary" strokeWidth={1.5} />
          </div>

          {/* Spinning dots */}
          <div className="absolute h-full w-full animate-spin">
            <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full bg-primary/80" />
            <div className="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 rounded-full bg-primary/60" />
            <div className="absolute left-full top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-primary/80" />
            <div className="absolute right-full top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-primary/60" />
          </div>
        </div>

        {/* Loading message */}
        <div className="mt-8 max-w-md transform px-4">
          <div className="rounded-lg bg-card/90 p-4 shadow-lg backdrop-blur">
            <p className="text-center text-base font-medium text-foreground/80">
              {message}
            </p>
            <div className="mt-2 flex justify-center">
              <div className="flex w-20 justify-between">
                <div
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/80"
                  style={{ animationDelay: '0ms' }}
                />
                <div
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/80"
                  style={{ animationDelay: '150ms' }}
                />
                <div
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/80"
                  style={{ animationDelay: '300ms' }}
                />
                <div
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary/80"
                  style={{ animationDelay: '450ms' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

LoadingStage.displayName = 'LoadingStage';

export default LoadingStage;

// Add these animations to your tailwind.config.js:
// extend: {
//   animation: {
//     'spin-slow': 'spin 3s linear infinite',
//     'reverse-spin': 'reverse-spin 2s linear infinite',
//     'bounce-subtle': 'bounce-subtle 2s ease-in-out infinite',
//     'pulse-slow': 'pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite',
//   },
//   keyframes: {
//     'reverse-spin': {
//       from: { transform: 'rotate(0deg)' },
//       to: { transform: 'rotate(-360deg)' },
//     },
//     'bounce-subtle': {
//       '0%, 100%': { transform: 'translateY(-5%)' },
//       '50%': { transform: 'translateY(5%)' },
//     },
//   },
// },
